import * as React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@/lib/utils';

const labelVariants = cva(
  'block font-heading text-sm font-medium leading-[1.3]',
  {
    variants: {
      context: {
        default: 'text-fg-primary',
        modal: 'text-[var(--color-neutral-25)]',
      },
    },
    defaultVariants: {
      context: 'default',
    },
  }
);

type LabelProps = React.ComponentProps<'label'> &
  VariantProps<typeof labelVariants> & {
    /**
     * Marks the field as optional with a small right-aligned "OPTIONAL" on
     * the same line as the label text. Hidden from assistive tech: the
     * accessible name stays the label text, and optionality is already
     * conveyed by the absence of `required`.
     */
    optional?: boolean;
  };

const Label = React.forwardRef<HTMLLabelElement, LabelProps>(
  ({ className, context, optional = false, children, ...props }, ref) => {
    return (
      <label
        ref={ref}
        className={cn(
          labelVariants({ context }),
          optional && 'flex items-baseline justify-between gap-3',
          className
        )}
        {...props}
      >
        {optional ? <span>{children}</span> : children}
        {optional && (
          <span
            data-slot="label-optional"
            aria-hidden="true"
            className="shrink-0 text-[0.6875rem] font-normal uppercase tracking-wide opacity-70"
          >
            Optional
          </span>
        )}
      </label>
    );
  }
);

Label.displayName = 'Label';

export { Label };
